/**
 * Device Detection Utility
 * 
 * Detects mobile devices to redirect users to app download page
 * ElectricNOW web app is designed for desktop/tablet experience only
 */

export const DeviceDetection = {
  /**
   * Check if the current device is a mobile phone
   * @returns {boolean} True if mobile device detected
   */
  isMobile: () => {
    // Check user agent for mobile indicators
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Mobile device patterns
    const mobilePatterns = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
      /Mobile/i,
      /mobile/i,
      /phone/i,
      /Phone/i
    ];
    
    // Check if any mobile pattern matches
    const isMobileUserAgent = mobilePatterns.some(pattern => pattern.test(userAgent));
    
    // Additional checks for screen size (mobile-like dimensions)
    const isMobileScreen = window.innerWidth <= 768 && window.innerHeight <= 1024;
    
    // Check for very small screens (definitely mobile)
    const isVerySmallScreen = window.innerWidth <= 480;
    
    // Check for touch capability (mobile indicator)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Enhanced detection for dev tools and simulators
    // If screen is very small (<=480px), consider it mobile regardless of user agent
    // Otherwise, require mobile user agent AND (small screen OR touch)
    return isVerySmallScreen || (isMobileUserAgent && (isMobileScreen || isTouchDevice));
  },

  /**
   * Check if device is specifically an iPhone
   * @returns {boolean} True if iPhone detected
   */
  isIPhone: () => {
    return /iPhone/i.test(navigator.userAgent);
  },

  /**
   * Check if device is specifically an Android phone
   * @returns {boolean} True if Android phone detected
   */
  isAndroid: () => {
    return /Android/i.test(navigator.userAgent) && /Mobile/i.test(navigator.userAgent);
  },

  /**
   * Check if device is an iPad (allow iPad usage)
   * @returns {boolean} True if iPad detected
   */
  isIPad: () => {
    return /iPad/i.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  },

  /**
   * Check if device should be blocked (mobile phones only, not tablets)
   * @returns {boolean} True if device should be redirected to app download
   */
  shouldRedirectToApp: () => {
    // Block mobile phones but allow tablets (iPad, Android tablets)
    const isMobile = DeviceDetection.isMobile();
    const isIPad = DeviceDetection.isIPad();
    const isAndroidTablet = /Android/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent);
    
    // Redirect if mobile but not a tablet
    return isMobile && !isIPad && !isAndroidTablet;
  },

  /**
   * Get device type for analytics/logging
   * @returns {string} Device type identifier
   */
  getDeviceType: () => {
    if (DeviceDetection.isIPhone()) return 'iPhone';
    if (DeviceDetection.isAndroid()) return 'Android';
    if (DeviceDetection.isIPad()) return 'iPad';
    if (/Android/i.test(navigator.userAgent)) return 'Android Tablet';
    if (/Macintosh/i.test(navigator.userAgent)) return 'Mac';
    if (/Windows/i.test(navigator.userAgent)) return 'Windows';
    return 'Desktop';
  },

  /**
   * Check if we're in Facebook's in-app browser
   * @returns {boolean} True if Facebook browser detected
   */
  isFacebookBrowser: () => {
    const userAgent = navigator.userAgent || '';
    return /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i.test(userAgent);
  },

  /**
   * Check if we're in Instagram's in-app browser
   * @returns {boolean} True if Instagram browser detected
   */
  isInstagramBrowser: () => {
    const userAgent = navigator.userAgent || '';
    return /Instagram/i.test(userAgent);
  },

  /**
   * Check if we're in any social media in-app browser
   * @returns {boolean} True if social media browser detected
   */
  isSocialMediaBrowser: () => {
    return DeviceDetection.isFacebookBrowser() || DeviceDetection.isInstagramBrowser();
  },

  /**
   * Get app store URL based on device type
   * @returns {object} Object with iOS and Android app store URLs
   */
  getAppStoreUrls: () => {
    return {
      ios: 'https://apps.apple.com/us/app/electricnow/id1501030946',
      android: 'https://play.google.com/store/apps/details?id=com.electric.now',
      fallback: 'https://electricnow.tv/download' // Fallback download page
    };
  }
};

export default DeviceDetection;

